import React, { useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";

import { API_URL } from "../../config/environment";
import { useTranslation } from "react-i18next";
import {
  _fetchFilteredIngredients,
  _fetchOptions,
  filterDataByField,
  filterDataByMultipleFields,
  filterDataByOccurrence,
  getIngredientsByTechnofunctionality,
} from "../../utils/descisionTreeUtils";
import { questionsConfig } from "../../helpers/ques";
import {
  removeKeyFromObjectState,
  removeKeysFromObjectState,
  updateObjectState,
  updateAndRemoveKeysFromObjectState,
} from "../../utils/stateUtils";
import CategorySelection from "./CategorySelection";
import Question from "./Question";
import SubmitButton from "./SubmitButton";
import { getLastKey } from "../../utils/formUtils";

const categoryCodePrefixes = {
  substitution_de_matiere_grasse: "FTS-",
  selection_des_antimicrobiens: "ANM-",
  substitution_des_sucres: "SGS-",
  selection_des_antioxydants: "ANX-",
  selection_des_epaississant: "TC",
  selection_des_moussants: "FOM-",
  selection_des_emulsifiants: "EML-",
};

const DecisionTree = ({ onSubmit, onReset }) => {
  const { t } = useTranslation();

  const [category, setCategory] = useState(null); // Track selected category
  // Track filtered results for each question
  const [questionsResults, setQuestionsResults] = useState({});

  // Dynamically translate the questions based on the selected category
  const questions = category
    ? questionsConfig[category].map((q) => ({
        ...q,
        text: t(q.text), // Translate the text key here
      }))
    : [];

  const [visibleQuestions, setVisibleQuestions] = useState(1); // Start with only the first question visible
  const [answers, setAnswers] = useState({}); // Store answers for each question
  const [selectedOptions, setSelectedOptions] = useState({}); // Initialize as an object
  const [options, setOptions] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(false); // Toggle to show/hide all questions
  const [page] = useState(0);
  const [pageSize] = useState(15);

  const [codePrefix, setCodePrefix] = useState(null);
  const [filteredIngredients, setFilteredIngredients] = useState([]);
  const [isFilteredIngredients, setIsFilteredIngredients] = useState(false);
  const [ingredientsFound, setIngredientsFound] = useState(0);

  // Function to handle category selection
  const handleCategorySelect = (selectedCategory) => {
    setCategory(selectedCategory);
    setCodePrefix(categoryCodePrefixes[selectedCategory]);
    setVisibleQuestions(1);
    setAnswers({});
    setSelectedOptions({});
    setOptions({});
    setQuestionsResults({});
    setIsCollapsed(false);
  };

  // Function to reset all answers and state
  const handleReset = () => {
    setCategory(null);
    setAnswers({});
    setSelectedOptions({});
    setOptions({});
    setQuestionsResults({});
    setVisibleQuestions(1);
    setIsCollapsed(false);

    // Clear the Decision Tree table in MainDashboard
    if (onReset) {
      onReset();
    }
  };

  // Fetch options based on answers and selectedOptions
  useEffect(() => {
    questions.forEach((question) => {
      if (
        answers[question.id] === "Yes" &&
        question.requiresDropdown &&
        !options[question.id]
      ) {
        fetchOptions(question.id, question.field, category);
      }
    });
  }, [answers, selectedOptions]);

  const fetchOptions = async (questionId, field, technofunctionality) => {
    const url = `${API_URL}/filter-options`;
    const optionsFn = await _fetchOptions(
      url,
      questionId,
      field,
      questions,
      answers,
      selectedOptions,
      technofunctionality
    );

    setOptions(optionsFn);
  };

  const fetchIngredients = async () => {
    const data = await getIngredientsByTechnofunctionality(category);
    setFilteredIngredients(data);
    setIsFilteredIngredients(false);
  };

  useEffect(() => {
    // console.log("Updated questionsResults:", questionsResults);
    const key = getLastKey(questionsResults);
    const lastQuestionsResult = questionsResults[key];
    setIngredientsFound(lastQuestionsResult?.length || 0);
  }, [questionsResults]);

  useEffect(() => {
    fetchIngredients();
  }, [category]);

  const fetchFilteredIngredients = async (
    field,
    relatedColumns,
    questionId
  ) => {
    const url = `${API_URL}/filter-options/filtered-data`;
    const data = await _fetchFilteredIngredients(
      url,
      field,
      relatedColumns,
      category
    );
    updateObjectState(setQuestionsResults, questionId, data);
  };

  // Updated handleAnswer function
  const handleAnswer = (questionId, answer) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);

    updateAndRemoveKeysFromObjectState(
      setAnswers,
      questions,
      questionId,
      answer,
      questionIndex + 1
    );

    if (answer === "Yes") {
      const question = questions[questionIndex];
      if (question.requiresDropdown) {
        updateObjectState(setSelectedOptions, questionId, "");
        if (question.hasFetchableOptions) {
          // Fetch options after state updates
          fetchOptions(questionId, question.field, category);
        } else {
          updateObjectState(setOptions, questionId, question.options);
        }
      }
    } else {
      removeKeyFromObjectState(setSelectedOptions, questionId);
      removeKeyFromObjectState(setOptions, questionId);

      // Clear filtered results for this question and subsequent questions
      removeKeysFromObjectState(setQuestionsResults, questions, questionIndex);
    }

    // Update visibleQuestions
    setVisibleQuestions(questionIndex + 2);
  };

  // Updated handleSelectChange function using functional updates
  const handleSelectChange = (questionId, label) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    const nextQuestionIndex = questionIndex + 1;

    // Update selected options and reset dependent states
    updateAndRemoveKeysFromObjectState(
      setSelectedOptions,
      questions,
      questionId,
      label,
      nextQuestionIndex
    );
    removeKeysFromObjectState(setAnswers, questions, nextQuestionIndex);
    removeKeysFromObjectState(setOptions, questions, nextQuestionIndex);
    removeKeysFromObjectState(
      setQuestionsResults,
      questions,
      nextQuestionIndex
    );

    // Fetch options for the next question if necessary
    const nextQuestion = questions[nextQuestionIndex];
    if (
      nextQuestion &&
      answers[nextQuestion.id] === "Yes" &&
      nextQuestion.requiresDropdown
    ) {
      fetchOptions(nextQuestion.id, nextQuestion.field, category);
    }

    // Extract field and related columns
    const field = questions[questionIndex].field;
    const isComplexFilter = questions[questionIndex]?.isComplexFilter;
    const isFilterByOccurrence = questions[questionIndex]?.isFilterByOccurrence;
    const relatedColumns = options[questionId]?.find(
      (opt) => opt.label === label
    )?.value;

    // Handle filtering logic
    if (questionId === 1) {
      fetchFilteredIngredients(field, relatedColumns, questionId);
      setIsFilteredIngredients(true);
    }

    if (questionId !== 1) {
      const questionResultsLength = Object.keys(questionsResults).length;
      let lastQuestionResultsIndex = null;

      if (questionResultsLength !== questionId) {
        if (getLastKey(questionsResults) > questionId) {
          delete questionsResults[getLastKey(questionsResults)];
        } else {
          delete questionsResults[questionId];
        }

        lastQuestionResultsIndex = getLastKey(questionsResults);
      } else {
        lastQuestionResultsIndex = questionId - 1;
      }

      // console.log("Last Question", lastQuestionResultsIndex);
      let data = null;
      let _questionsResults = null;

      if (lastQuestionResultsIndex) {
        _questionsResults = questionsResults[lastQuestionResultsIndex];
      } else {
        _questionsResults = filteredIngredients;
        setIsFilteredIngredients(true);
      }

      if (isFilterByOccurrence) {
        data = filterDataByOccurrence(
          _questionsResults,
          questions[questionIndex].valueFields,
          relatedColumns
        );
      }

      if (isComplexFilter) {
        data = filterDataByMultipleFields(
          _questionsResults,
          questions[questionIndex].properties,
          relatedColumns,
          questions[questionIndex].propertyFields,
          questions[questionIndex].valueFields
        );
      }

      if (!isComplexFilter && !isFilterByOccurrence) {
        data = filterDataByField(_questionsResults, field, relatedColumns);
      }

      if (data.length === 0) {
        alert(
          "Your selection does not have any data, please try another selection."
        );
      }

      updateObjectState(
        setQuestionsResults,
        questionId,
        data.length > 0 ? data : []
      );
    }

    // Update visible questions
    setVisibleQuestions(nextQuestionIndex + 1);
  };

  const toggleShowAllQuestions = () => {
    if (isCollapsed) {
      // Show questions
      const answeredQuestions = Object.keys(answers).length;
      const newVisibleQuestions = Math.min(
        answeredQuestions + 1,
        questions.length
      );
      setVisibleQuestions(newVisibleQuestions);
      setIsCollapsed(false);
    } else {
      // Hide questions
      setIsCollapsed(true);
    }
  };

  const handleSubmit = () => {
    try {
      let results = null;

      const lastQuestionResultsIndex = getLastKey(questionsResults);
      const lastQuestionResults = questionsResults[lastQuestionResultsIndex];

      if (lastQuestionResults) {
        results = lastQuestionResults;
      } else {
        // const filteredIngredients = secureLocalStorage.getItem(
        //   "filteredIngredients"
        // );
        // results = JSON.parse(filteredIngredients);

        results = filteredIngredients;
      }

      onSubmit(results);
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <Box>
      <CategorySelection
        category={category}
        handleCategorySelect={handleCategorySelect}
        handleReset={handleReset}
        t={t}
      />

      {category && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={toggleShowAllQuestions}
              variant="contained"
              sx={{ marginBottom: 2 }}
            >
              {t(
                `dashboard.decision-tree.ShowHide.${
                  isCollapsed ? "show" : "hide"
                }`
              )}
            </Button>

            <Typography>
              {`${ingredientsFound} ${
                ingredientsFound > 1 ? "Ingredients" : "Ingredient"
              } `}
            </Typography>
          </Box>

          {!isCollapsed &&
            questions
              .slice(0, visibleQuestions)
              .map((question, index) => (
                <Question
                  key={question.id}
                  question={question}
                  index={index}
                  answers={answers}
                  selectedOptions={selectedOptions}
                  options={options}
                  handleAnswer={handleAnswer}
                  handleSelectChange={handleSelectChange}
                  t={t}
                />
              ))}

          <SubmitButton
            answers={answers}
            questions={questions}
            selectedOptions={selectedOptions}
            handleSubmit={handleSubmit}
            t={t}
          />
        </>
      )}
    </Box>
  );
};

export default DecisionTree;
